/* ─────────────────────────────────────────────────────────────
   Design tokens rendered by `/ds`.

   Scoped to what the page actually documents. The previous version also carried
   feature-colour semantics, usage counts, layout patterns and findings for the
   pre-Graphite shell; those went with the page that rendered them and are in git
   history if needed.
   ───────────────────────────────────────────────────────────── */

export interface TypographyToken {
  name: string
  usage: string
  className: string
  sample: string
}

export interface ScaleToken {
  token: string
  px: number
  usage: string
}

export interface VisualToken {
  token: string
  className: string
  usage: string
}

export const typographyScale: TypographyToken[] = [
  {
    name: "H1",
    usage: "Route titles like Jobs, Candidates, Community",
    className: "text-3xl font-bold tracking-tight",
    sample: "Design System",
  },
  {
    name: "H2",
    usage: "Large section headings and dialog headers",
    className: "text-2xl font-bold",
    sample: "Component Gallery",
  },
  {
    name: "H3",
    usage: "Prominent card titles and panel headings",
    className: "text-xl font-semibold",
    sample: "Candidate Summary",
  },
  {
    name: "H4",
    usage: "Subsections such as pipeline and modal headings",
    className: "text-lg font-semibold",
    sample: "Candidate Pipeline",
  },
  {
    name: "H5",
    usage: "Labels inside cards, menus, and forms",
    className: "text-base font-medium",
    sample: "Overview",
  },
  {
    name: "H6",
    usage: "Compact labels and table headings",
    className: "text-sm font-medium",
    sample: "Status",
  },
  {
    name: "Body",
    usage: "Default copy across forms, tables, and cards",
    className: "text-sm",
    sample: "Recruiter OS relies heavily on compact body copy to keep dense workflows readable.",
  },
  {
    name: "Caption",
    usage: "Metadata, helper text, and badge labels",
    className: "text-xs text-muted-foreground",
    sample: "Updated 5 minutes ago",
  },
]

export const spacingScale: ScaleToken[] = [
  { token: "1", px: 4, usage: "Tight icon and badge gaps" },
  { token: "2", px: 8, usage: "Default control spacing" },
  { token: "3", px: 12, usage: "Card internals and stacked metadata" },
  { token: "4", px: 16, usage: "Default layout gap and padding" },
  { token: "6", px: 24, usage: "Section spacing and card padding" },
  { token: "8", px: 32, usage: "Large route padding" },
]

export const radiusScale: VisualToken[] = [
  { token: "rounded-sm", className: "rounded-sm", usage: "Menu items and tight interactive controls" },
  { token: "rounded-md", className: "rounded-md", usage: "Buttons, inputs, tabs, tables, menus" },
  { token: "rounded-lg", className: "rounded-lg", usage: "Cards, empty states, larger grouped surfaces" },
  { token: "rounded-xl", className: "rounded-xl", usage: "Rare emphasis surfaces" },
  { token: "rounded-full", className: "rounded-full", usage: "Avatars, pills, status dots" },
]

/* ─────────────────────────────────────────────────────────────
   Graphite. Sourced from the canvas artboards in
   "Reimagining Recruiter OS.dc.html" — these were never promoted into the
   design system project's own tokens/colors.css, which still documents the
   pre-Graphite app.
   ───────────────────────────────────────────────────────────── */

export interface SurfaceLevel {
  name: string
  hex: string
  token: string
  className: string
  usage: string
}

/**
 * The rule the canvas states with no exceptions: chrome is the frame, canvas is
 * the gap, and every piece of content sits on a surface. Note the direction —
 * chrome is *darker* than the page and surfaces are *lighter*, which is why
 * `background` + `card` alone could not express it.
 */
export const surfaceLevels: SurfaceLevel[] = [
  {
    name: "Chrome",
    hex: "#08090B",
    token: "--chrome",
    className: "bg-chrome",
    usage: "Top bar and conversation rail — the frame around everything",
  },
  {
    name: "Canvas",
    hex: "#0E1013",
    token: "--background",
    className: "bg-background",
    usage: "Each page's content area — the gap between surfaces",
  },
  {
    name: "Surface",
    hex: "#14171B",
    token: "--card",
    className: "bg-card",
    usage: "Tables, cards, task lists, the record rail",
  },
  {
    name: "Hairline",
    hex: "#23282F",
    token: "--border",
    className: "bg-border",
    usage: "Every separator — the system uses hairlines, not shadows",
  },
]

export interface StageColor {
  name: string
  hex: string
  token: string
}

/** Eight stages, colours preserved from the existing pipeline. */
export const pipelineStageColors: StageColor[] = [
  { name: "Longlist", hex: "#64748B", token: "--stage-longlist" },
  { name: "Shortlist", hex: "#3B82F6", token: "--stage-shortlist" },
  { name: "Outbound", hex: "#A855F7", token: "--stage-outbound" },
  { name: "Screening", hex: "#F97316", token: "--stage-screening" },
  { name: "Submitted", hex: "#EAB308", token: "--stage-submitted" },
  { name: "Interviewing", hex: "#14B8A6", token: "--stage-interviewing" },
  { name: "Rejected", hex: "#EF4444", token: "--stage-rejected" },
  { name: "Hired", hex: "#22C55E", token: "--stage-hired" },
]

export interface TypeRuleExample {
  value: string
  kind: "language" | "machine"
  why: string
}

/**
 * Inter carries language, JetBrains Mono carries machine values. The edge cases
 * are what make it a rule rather than a vibe: a section label is language even
 * when it sits above numbers, and an activity row's primitive name is machine
 * output even though it reads like a word.
 */
export const typeRuleExamples: TypeRuleExample[] = [
  { value: "Money at rest", kind: "language", why: "A section label is language — Inter medium, not mono uppercase" },
  { value: "$26,400", kind: "machine", why: "Money readout" },
  { value: "Senior Platform Engineer", kind: "language", why: "A name a person wrote" },
  { value: "16", kind: "machine", why: "Count" },
  { value: "PLC-1042", kind: "machine", why: "Record id" },
  { value: "stage moved", kind: "machine", why: "An activity row's primitive name is machine output, despite reading as words" },
  { value: "09:14", kind: "machine", why: "Timestamp" },
  { value: "Chase Northwind first", kind: "language", why: "Robin's prose" },
  { value: "⌘K", kind: "machine", why: "Keyboard shortcut" },
]
